import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, Clock, Users, Zap, ShoppingCart } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MONTANA_ADDONS_CATALOG } from '@/data/addons/catalog.montana';

interface WelcomePageLayoutProps {
  tier: 'silver' | 'gold' | 'custom';
  children: React.ReactNode;
  onContinue: () => void;
  backUrl?: string;
}

export const WelcomePageLayout: React.FC<WelcomePageLayoutProps> = ({
  tier,
  children,
  onContinue,
  backUrl = '/pricing/montana',
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get selected addons from URL parameters
  useEffect(() => {
    const addonsParam = searchParams.get('addons');
    if (addonsParam) {
      const ids = addonsParam
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      setSelectedAddons(ids);
    }
  }, [searchParams]);

  const handleBack = () => {
    // Prefer same-origin referrer (exact previous page), then history.back(), then fallback to backUrl.
    try {
      const referrer = document.referrer;

      if (referrer) {
        try {
          const refOrigin = new URL(referrer).origin;
          // Only navigate to referrer if it's same-origin to avoid cross-site redirects
          if (refOrigin === window.location.origin) {
            window.location.href = referrer;
            return;
          }
        } catch (e) {
          // If URL parsing fails, ignore and fall through to history/backUrl fallback
        }
      }

      // If user has a history stack, go back one step
      if (window.history.length > 1) {
        window.history.back();
        return;
      }
    } catch (e) {
      // ignore and fallback
    }

    // Final fallback: use the provided backUrl (relative path) via router navigation
    navigate(backUrl);
  };

  const getTierColor = () => {
    switch (tier) {
      case 'silver':
        return 'from-gray-600 to-gray-800';
      case 'gold':
        return 'from-blue-600 to-blue-800';
      case 'custom':
        return 'from-purple-600 to-purple-800';
      default:
        return 'from-gray-600 to-gray-800';
    }
  };

  const getTierName = () => {
    switch (tier) {
      case 'silver':
        return 'Silver Package';
      case 'gold':
        return 'Gold Package';
      case 'custom':
        return 'Custom Package';
      default:
        return 'Package';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white shadow-xs border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Pricing
            </Button>
            <div className="text-sm text-gray-500">Step 1 of 5: Welcome</div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className={`bg-linear-to-br ${getTierColor()} text-white py-16`}>
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Congratulations on Taking This Step Forward!
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Your community's evolution of progress starts here with the{' '}
              <strong>{getTierName()}</strong>
            </p>

            {/* Progress indicators */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="flex flex-col items-center">
                <div className="bg-white/20 rounded-full p-4 mb-4">
                  <Clock className="w-8 h-8" />
                </div>
                <h3 className="font-semibold mb-2">Implementation Timeline</h3>
                <p className="text-sm text-blue-100">Scoped to your community's needs</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-white/20 rounded-full p-4 mb-4">
                  <Users className="w-8 h-8" />
                </div>
                <h3 className="font-semibold mb-2">Dedicated Support</h3>
                <p className="text-sm text-blue-100">Personal community success team</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-white/20 rounded-full p-4 mb-4">
                  <Zap className="w-8 h-8" />
                </div>
                <h3 className="font-semibold mb-2">Revolutionary Results</h3>
                <p className="text-sm text-blue-100">Immediate efficiency & wealth building</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Selected Add-ons Display */}
            {selectedAddons.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Selected Add-ons</h3>
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                    {selectedAddons.length}
                  </span>
                </div>
                <div className="grid gap-2">
                  {selectedAddons.map((addonId) => {
                    const addon = MONTANA_ADDONS_CATALOG.find((a) => a.id === addonId);
                    return addon ? (
                      <div key={addonId} className="flex items-center gap-2 text-sm text-blue-800">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">{addon.name}</span>
                        <span className="text-blue-600">({addon.badge})</span>
                      </div>
                    ) : null;
                  })}
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  These will be included in your assessment and proposal. You can adjust them during
                  the process.
                </p>
              </div>
            )}

            {children}

            {/* Continue Button */}
            <div className="text-center mt-12">
              <Button
                onClick={() => navigate('/onboarding')}
                size="lg"
                className={`bg-linear-to-r ${getTierColor()} text-white px-8 py-4 text-lg font-semibold`}
              >
                Continue to Assessment →
              </Button>
              <p className="text-sm text-gray-500 mt-4">
                Next: Tell us about your community (5-10 minutes)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
